import React, { useEffect, useMemo, useRef, useState } from "react";
import { fleetOpsApi as A } from "../api/fleetOpsApi";
import { getUserRole } from "../../../shared/lib/auth/authHelper";
import s from "../pages/FleetOps.module.css";

const norm = (value) => String(value ?? "").trim().toLowerCase();

export default function FleetScopeUserPicker({ value = "", onChange, t }) {
  const text = (key, fallback, options = {}) => t(key, { defaultValue: fallback, ...options });
  const isSuperadmin = norm(getUserRole()) === "superadmin";
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isSuperadmin) return undefined;
    let alive = true;
    setLoading(true);
    setError("");
    A.scopeUsers()
      .then((response) => {
        if (!alive) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setUsers(list);
        if (value && !list.some((user) => String(user.id) === String(value))) {
          onChange?.("");
        }
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || text("fleet_scope_load_error", "Не удалось загрузить пользователей"));
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [isSuperadmin]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("mousedown", close);
    };
  }, [open]);

  const selected = useMemo(
    () => users.find((user) => String(user.id) === String(value)) || null,
    [users, value]
  );

  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return users;
    return users.filter((user) => {
      const haystack = [
        user.id,
        user.ui_id,
        user.full_name,
        user.email,
        user.vehicles,
        user.files,
      ].map(norm).join(" ");
      return haystack.includes(q);
    });
  }, [users, query]);

  if (!isSuperadmin) return null;

  const choose = (userId) => {
    const next = userId ? String(userId) : "";
    onChange?.(next);
    try { sessionStorage.setItem("fleetops:superadmin-scope-user", next); } catch {}
    setQuery("");
    setOpen(false);
  };

  const selectedLabel = selected
    ? `${selected.ui_id} · ${selected.full_name || selected.email || `#${selected.id}`}`
    : text("fleet_scope_all_users", "Все пользователи");

  return (
    <div className={s.fleetScopePicker} ref={rootRef}>
      <div className={s.fleetScopePickerLabel}>{text("fleet_scope_user", "Пользователь")}</div>
      <button
        type="button"
        className={`${s.fleetScopePickerButton} ${open ? s.fleetScopePickerButtonOpen : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={s.fleetScopePickerIdentity}>
          <strong>{selectedLabel}</strong>
          <small>
            {selected
              ? text("fleet_scope_counts", "Авто: {{vehicles}} · Файлы: {{files}}", { vehicles: selected.vehicles || 0, files: selected.files || 0 })
              : text("fleet_scope_all_hint", "Сводные данные всех пользователей")}
          </small>
        </span>
        <span className={s.fleetScopePickerChevron} aria-hidden="true">⌄</span>
      </button>

      {open && (
        <div className={s.fleetScopePickerMenu}>
          <div className={s.fleetScopePickerSearch}>
            <span aria-hidden="true">⌕</span>
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={text("fleet_scope_search", "Поиск по имени, email или ID…")}
            />
          </div>
          <div className={s.fleetScopePickerList} role="listbox">
            <button
              type="button"
              role="option"
              aria-selected={!value}
              className={`${s.fleetScopePickerOption} ${!value ? s.fleetScopePickerOptionActive : ""}`}
              onClick={() => choose("")}
            >
              <span className={s.fleetScopePickerAvatar}>Σ</span>
              <span className={s.fleetScopePickerOptionText}>
                <strong>{text("fleet_scope_all_users", "Все пользователи")}</strong>
                <small>{text("fleet_scope_all_hint", "Сводные данные всех пользователей")}</small>
              </span>
            </button>
            {loading && <div className={s.fleetScopePickerEmpty}>{text("loading", "Загрузка…")}</div>}
            {!loading && error && <div className={s.fleetScopePickerError}>{error}</div>}
            {!loading && !error && filtered.map((user) => (
              <button
                type="button"
                role="option"
                aria-selected={String(user.id) === String(value)}
                key={user.id}
                className={`${s.fleetScopePickerOption} ${String(user.id) === String(value) ? s.fleetScopePickerOptionActive : ""}`}
                onClick={() => choose(user.id)}
              >
                <span className={s.fleetScopePickerAvatar}>{String(user.full_name || user.email || "U").trim().slice(0, 1).toUpperCase()}</span>
                <span className={s.fleetScopePickerOptionText}>
                  <strong>{user.ui_id} · {user.full_name || user.email || `#${user.id}`}</strong>
                  <small>{user.email || "—"}</small>
                </span>
                <span className={s.fleetScopePickerCounts}>
                  <b>🚗 {user.vehicles || 0}</b>
                  <b>📄 {user.files || 0}</b>
                </span>
              </button>
            ))}
            {!loading && !error && !filtered.length && (
              <div className={s.fleetScopePickerEmpty}>{text("fleet_scope_empty", "Пользователи не найдены")}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
